import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useChatStore } from '@/store';
import { useFloatEntryDrag } from '@/hooks';
import { CloseIcon, MagicAIIcon } from '@/assets/SVGIcon';
import { t } from '@/i18n';
import { dismissTaglineForDays, isTaglineDismissed } from '@/utils/floatTaglineDismiss';
import { emitGa } from '@/utils/ga';
import './index.less';

interface AiFloatButtonProps {
  /** 仅 SDK 悬浮入口：点开抽屉时上报 magic_ai_click */
  trackClick?: boolean;
}

const TAGLINE_REVEAL_DELAY_MS = 80;

const AiFloatButton: React.FC<AiFloatButtonProps> = ({ trackClick = false }) => {
  const isOpen = useChatStore((s) => s.isOpen);
  const openDrawer = useChatStore((s) => s.openDrawer);
  const closeDrawer = useChatStore((s) => s.closeDrawer);
  const identityUid = useChatStore((s) => s.identityUid);
  const historyScope = useChatStore((s) => s.historyScope);
  const hostInitialized = useChatStore((s) => s.hostInitialized);
  const floatEntryDismissed = useChatStore((s) => s.floatEntryDismissed);
  const floatEntryRouteHidden = useChatStore((s) => s.floatEntryRouteHidden);
  const dismissFloatEntry = useChatStore((s) => s.dismissFloatEntry);
  const [taglineDismissed, setTaglineDismissed] = useState(() => isTaglineDismissed());
  const [showDismissTip, setShowDismissTip] = useState(false);
  const [taglineEntered, setTaglineEntered] = useState(false);
  const entryRef = useRef<HTMLDivElement>(null);
  const taglineText = t('aiButton.tagline');
  const {
    positionStyle,
    isDragging,
    hasCustomPosition,
    handleDragPointerDown,
    handleDragPointerMove,
    handleDragPointerUp,
    handleDragPointerCancel,
    shouldSuppressClick,
  } = useFloatEntryDrag(entryRef);

  const showTagline = !isOpen && !taglineDismissed;

  useEffect(() => {
    setTaglineDismissed(isTaglineDismissed());
  }, [identityUid, historyScope]);

  useEffect(() => {
    if (!showTagline) {
      setTaglineEntered(false);
      return;
    }

    const enterTimer = window.setTimeout(() => setTaglineEntered(true), TAGLINE_REVEAL_DELAY_MS);
    return () => window.clearTimeout(enterTimer);
  }, [showTagline, taglineText]);

  const entryMounted = hostInitialized && !floatEntryDismissed && !floatEntryRouteHidden;
  const loadedReportedRef = useRef(false);

  useEffect(() => {
    if (!entryMounted || loadedReportedRef.current) return;
    loadedReportedRef.current = true;
    emitGa('magic_ai_loaded');
  }, [entryMounted]);

  useEffect(() => {
    if (!showDismissTip) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!entryRef.current?.contains(e.target as Node)) {
        setShowDismissTip(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [showDismissTip]);

  const handleTaglineClose = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDismissTip(true);
  }, []);

  const handleDismiss = useCallback((days: number) => {
    dismissTaglineForDays(days);
    setTaglineDismissed(true);
    setShowDismissTip(false);
  }, []);

  const handleToggleDrawer = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      if (shouldSuppressClick()) return;
      if (isOpen) {
        closeDrawer();
      } else {
        if (trackClick) emitGa('magic_ai_click');
        openDrawer();
      }
    },
    [shouldSuppressClick, isOpen, openDrawer, closeDrawer, trackClick],
  );

  const handleHideEntry = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      dismissFloatEntry();
    },
    [dismissFloatEntry],
  );

  if (!hostInitialized || floatEntryDismissed || floatEntryRouteHidden) {
    return null;
  }

  return (
    <div
      ref={entryRef}
      className={`chat-bi-float-entry${isOpen ? ' chat-bi-float-entry--active' : ''}${
        hasCustomPosition ? ' chat-bi-float-entry--custom-position' : ''
      }${isDragging ? ' chat-bi-float-entry--dragging' : ''}`}
      style={positionStyle}
    >
      {showTagline && (
        <div
          className={`chat-bi-float-entry__promo${taglineEntered ? ' chat-bi-float-entry__promo--visible' : ''}`}
        >
          <div className="chat-bi-float-entry__promo-grow">
            <div className="chat-bi-float-entry__tagline">
              <span className="chat-bi-float-entry__tagline-text">{taglineText}</span>
              <button
                type="button"
                className="chat-bi-float-entry__tagline-close"
                aria-label={t('aiButton.dismissClose')}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={handleTaglineClose}
              >
                <CloseIcon />
              </button>
            </div>
          </div>
          {showDismissTip && (
            <div
              className="chat-bi-float-entry__dismiss-tip"
              role="dialog"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <p className="chat-bi-float-entry__dismiss-tip-text">{t('aiButton.dismissPrompt')}</p>
              <div className="chat-bi-float-entry__dismiss-tip-actions">
                <button
                  type="button"
                  className="chat-bi-float-entry__dismiss-tip-btn"
                  onClick={() => handleDismiss(7)}
                >
                  {t('aiButton.dismissWeek')}
                </button>
                <button
                  type="button"
                  className="chat-bi-float-entry__dismiss-tip-btn"
                  onClick={() => handleDismiss(30)}
                >
                  {t('aiButton.dismissMonth')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="chat-bi-float-entry__btn-wrap">
        <button
          type="button"
          className="chat-bi-float-entry__entry-close"
          aria-label={t('aiButton.close')}
          onClick={handleHideEntry}
        >
          <CloseIcon />
        </button>
        <button
          type="button"
          className="chat-bi-float-entry__btn"
          onClick={handleToggleDrawer}
          onPointerDown={handleDragPointerDown}
          onPointerMove={handleDragPointerMove}
          onPointerUp={handleDragPointerUp}
          onPointerCancel={handleDragPointerCancel}
          aria-label={isOpen ? t('aiButton.close') : t('aiButton.open')}
        >
          <span className="chat-bi-float-entry__icon">
            <MagicAIIcon />
          </span>
        </button>
      </div>
    </div>
  );
};

export default AiFloatButton;
